import React, { useState } from 'react';
import { Container, Row, Col, Modal, Image, Button } from 'react-bootstrap';
import { FaMapMarkerAlt, FaClipboardList, FaLandmark, FaUsers, FaClock } from 'react-icons/fa';
import { motion } from 'framer-motion';
import SEO from '../components/SEO';
import communityData from '../data/communityData.json';

const images = require.context('../assets', false, /\.(png|jpe?g|svg)$/);

const Community = () => {
    const [showRegisterModal, setShowRegisterModal] = useState(false);
    const [showMapModal, setShowMapModal] = useState(false);
    const [selectedZone, setSelectedZone] = useState(null);

    const handleIconClick = (zone) => {
        setSelectedZone(zone);
        setShowMapModal(true);
    };

    const handleRegisterClick = () => {
        setShowRegisterModal(true);
    };

    // Process data to attach actual images
    const territories = communityData.map(zone => {
        let imageSrc = null;
        try {
            imageSrc = images(`./${zone.imageFileName}`);
        } catch (err) {
            console.error(`Image not found: ${zone.imageFileName}`, err);
            // Fallback to a default if known, or just null
            try { imageSrc = images('./logo.png') } catch (e) { }
        }
        return {
            ...zone,
            image: imageSrc
        };
    });

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: { opacity: 1, y: 0 }
    };

    return (
        <div style={{ minHeight: '100vh', paddingTop: '60px' }}>
            <SEO
                title="Community & Server Map"
                description="Explore the Thalai Nagaram RP (TNRP) community, server map zones, community events, Discord social links, and player guidelines."
                keywords="TNRP Community, Thalai Nagaram RP Map, Tamil FiveM Community, GTA 5 Tamil RP Events, TNRP Discord, FiveM Tamil Server Map"
                canonicalUrl="https://thalainagaram.org/community"
            />
            <Container fluid className="py-5">
                <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-center mb-5"
                >
                    <h1 className="display-4 fw-bold text-white mb-2" style={{ fontFamily: 'Orbitron, sans-serif', textShadow: '0 0 15px rgba(188, 19, 254, 0.5)', fontSize: '2rem' }}>COMMUNITY <span className="text-neon-pink">MAP</span></h1>
                    <p className="lead text-secondary mb-4">Build the community. Define the city.</p>
                    <Button
                        variant="outline-light"
                        className="d-inline-flex align-items-center gap-2 px-4 py-3 fw-bold"
                        style={{
                            fontSize: '1rem',
                            borderColor: 'var(--neon-gold)',
                            color: 'var(--neon-gold)',
                            boxShadow: '0 0 20px rgba(255, 215, 0, 0.2)',
                            fontFamily: 'Orbitron, sans-serif',
                            borderRadius: '0',
                            letterSpacing: '1px'
                        }}
                        onClick={handleRegisterClick}
                    >
                        <FaClipboardList size={20} /> REGISTER YOUR COMMUNITY
                    </Button>
                    <p className="mt-3 text-secondary small fst-italic">
                        * Click the location icons for the location image
                    </p>
                </motion.div>

                <motion.div variants={containerVariants} initial="hidden" animate="visible">
                    <Row className="g-4">
                        {territories.map((zone, idx) => (
                            <Col md={6} lg={3} key={idx}>
                                <motion.div
                                    variants={itemVariants}
                                    whileHover={{ y: -5, scale: 1.02 }}
                                    className="position-relative overflow-hidden neon-card p-0"
                                    style={{
                                        height: '220px',
                                        cursor: 'default',
                                        border: `1px solid ${zone.color}`
                                    }}
                                >
                                    {/* Map Grid Pattern Overlay */}
                                    <div style={{
                                        position: 'absolute', inset: 0,
                                        backgroundImage: 'linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)',
                                        backgroundSize: '40px 40px', opacity: 0.2
                                    }} />

                                    <div className="p-4 position-relative h-100 d-flex flex-column justify-content-between z-1">
                                        <div className="d-flex justify-content-between align-items-start">
                                            <motion.div
                                                className="p-2 rounded-0 d-flex align-items-center justify-content-center"
                                                style={{ border: `1px solid ${zone.color}`, color: zone.color, background: 'rgba(0,0,0,0.5)', cursor: 'pointer', zIndex: 10 }}
                                                whileHover={{
                                                    scale: 1.2,
                                                    rotate: 15,
                                                    boxShadow: `0 0 15px ${zone.color}`,
                                                    background: 'rgba(255, 255, 255, 0.1)'
                                                }}
                                                whileTap={{ scale: 0.9 }}
                                                onClick={(e) => { e.stopPropagation(); handleIconClick(zone); }}
                                                title="View Map"
                                            >
                                                <FaMapMarkerAlt size={20} />
                                            </motion.div>
                                            <span className="badge rounded-0 letter-spacing-1 text-uppercase"
                                                style={{
                                                    background: 'rgba(0, 0, 0, 0.8)',
                                                    color: zone.color,
                                                    border: `1px solid ${zone.color}`,
                                                    fontWeight: '700',
                                                    boxShadow: `0 0 5px ${zone.color}40`
                                                }}>
                                                {zone.status}
                                            </span>
                                        </div>

                                        <div>
                                            <h3 className="h3 fw-bold text-white mb-0" style={{ fontFamily: 'Orbitron, sans-serif', textShadow: `0 0 10px ${zone.color}` }}>{zone.name}</h3>
                                            <div style={{ height: '2px', width: '100%', background: `linear-gradient(90deg, ${zone.color}, transparent)`, marginTop: '10px' }} />
                                        </div>
                                    </div>

                                    {/* Cyber Corner Accents */}
                                    <div style={{ position: 'absolute', bottom: 0, right: 0, width: '0', height: '0', borderStyle: 'solid', borderWidth: '0 0 40px 40px', borderColor: `transparent transparent ${zone.color} transparent`, opacity: 0.5 }} />

                                </motion.div>
                            </Col>
                        ))}

                    </Row>
                </motion.div>
            </Container>

            {/* Community Registration Modal */}
            <Modal
                show={showRegisterModal}
                onHide={() => setShowRegisterModal(false)}
                centered
                size="lg"
                className="cyber-modal"
            >
                <Modal.Header
                    closeButton
                    className="border-bottom-0 p-4"
                    style={{ background: '#0a0a0f', color: '#fff' }}
                >
                    <Modal.Title style={{ fontFamily: 'Orbitron, sans-serif', color: 'var(--neon-gold)', letterSpacing: '1px' }}>
                        <FaLandmark className="me-2" style={{ color: 'var(--neon-gold)' }} />
                        COMMUNITY REGISTRATION
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-4" style={{ background: '#0c0d14', color: '#e0e0e0' }}>
                    <div className="text-center mb-4">
                        <div
                            className="d-inline-flex align-items-center justify-content-center p-3 rounded-circle mb-3"
                            style={{
                                background: 'rgba(255, 215, 0, 0.1)',
                                border: '1px solid var(--neon-gold)',
                                boxShadow: '0 0 20px rgba(255, 215, 0, 0.2)',
                                width: '70px',
                                height: '70px'
                            }}
                        >
                            <FaLandmark size={32} style={{ color: 'var(--neon-gold)' }} />
                        </div>
                        <h3 className="fw-bold text-white mb-2" style={{ fontFamily: 'Orbitron, sans-serif' }}>
                            Visit TN Govt Office
                        </h3>
                        <p className="lead text-secondary mx-auto" style={{ maxWidth: '600px', fontSize: '1.05rem', lineHeight: '1.6' }}>
                            To register your community in <strong className="text-white">Thalai Nagaram RP</strong>, please visit the <strong style={{ color: 'var(--neon-gold)' }}>TN Govt Office</strong> in-game with your team and wait for Government Officer approval.
                        </p>
                    </div>

                    <div
                        className="p-4 mb-3 rounded-0"
                        style={{
                            background: 'rgba(15, 18, 28, 0.8)',
                            borderLeft: '4px solid var(--neon-gold)',
                            borderTop: '1px solid rgba(255,255,255,0.05)',
                            borderRight: '1px solid rgba(255,255,255,0.05)',
                            borderBottom: '1px solid rgba(255,255,255,0.05)'
                        }}
                    >
                        <h6 className="fw-bold text-white mb-3 text-uppercase" style={{ fontFamily: 'Orbitron, sans-serif', letterSpacing: '1px', fontSize: '0.9rem' }}>
                            Registration Instructions:
                        </h6>
                        <div className="d-flex flex-column gap-3">
                            <div className="d-flex align-items-start gap-3">
                                <div className="p-2 rounded" style={{ background: 'rgba(188, 19, 254, 0.15)', color: 'var(--neon-pink)' }}>
                                    <FaUsers size={18} />
                                </div>
                                <div>
                                    <strong className="text-white d-block mb-1">Assemble Your Team</strong>
                                    <span className="text-secondary small">Gather your core community leadership and team members in-game.</span>
                                </div>
                            </div>

                            <div className="d-flex align-items-start gap-3">
                                <div className="p-2 rounded" style={{ background: 'rgba(255, 215, 0, 0.15)', color: 'var(--neon-gold)' }}>
                                    <FaLandmark size={18} />
                                </div>
                                <div>
                                    <strong className="text-white d-block mb-1">Visit TN Govt Office</strong>
                                    <span className="text-secondary small">Head over to the official TN Govt Office location in-game together with your team.</span>
                                </div>
                            </div>

                            <div className="d-flex align-items-start gap-3">
                                <div className="p-2 rounded" style={{ background: 'rgba(0, 243, 255, 0.15)', color: 'var(--neon-cyan)' }}>
                                    <FaClock size={18} />
                                </div>
                                <div>
                                    <strong className="text-white d-block mb-1">Government Officer Approval</strong>
                                    <span className="text-secondary small">Submit your application to the active Govt Officer on duty and wait for official verification & approval.</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer className="border-top-0 p-3 justify-content-center" style={{ background: '#0a0a0f' }}>
                    <Button
                        variant="outline-light"
                        onClick={() => setShowRegisterModal(false)}
                        className="fw-bold px-5 py-2"
                        style={{
                            fontFamily: 'Orbitron, sans-serif',
                            borderColor: 'var(--neon-gold)',
                            color: 'var(--neon-gold)',
                            borderRadius: '0',
                            letterSpacing: '1px'
                        }}
                    >
                        UNDERSTOOD
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Map Modal */}
            <Modal show={showMapModal} onHide={() => setShowMapModal(false)} centered size="lg" className="cyber-modal">
                <Modal.Header closeButton className="border-bottom-0" style={{ background: '#0a0a0f', color: '#fff' }}>
                    <Modal.Title style={{ fontFamily: 'Orbitron, sans-serif' }}>
                        <FaMapMarkerAlt className="me-2" style={{ color: selectedZone?.color }} />
                        {selectedZone?.name} Area
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-0 text-center" style={{ background: '#000' }}>
                    <div style={{ position: 'relative', width: '100%', padding: '20px' }}>
                        <Image
                            src={selectedZone?.image}
                            fluid
                            style={{ maxHeight: '70vh', objectFit: 'contain' }}
                            alt={`${selectedZone?.name} Map`}
                        />
                        <div className="text-secondary mt-2 small">Detailed territory map.</div>
                    </div>
                </Modal.Body>
            </Modal>

            <style>{`
                .cyber-modal .modal-content {
                    background: #0a0a0f;
                    border: 1px solid var(--neon-gold);
                    box-shadow: 0 0 20px rgba(255, 215, 0, 0.2);
                    border-radius: 0;
                }
                .cyber-modal .btn-close {
                    filter: invert(1);
                }
            `}</style>
        </div >
    );
};

export default Community;
